import { useCallback, useEffect, useRef, useState } from 'react';
import { Env } from '../../shared';
import { ApiError, useAdminApi } from '../../shared/adminApi';
import { ReportAction, ReportStatus, UserReport } from './types';

/**
 * The user report queue (SSA-1579 over SSA-1578): one status at a time, oldest
 * first, reloaded when the env or status changes.
 *
 * Reports carry personal data, so nothing here logs them or keeps them beyond
 * component state.
 */
export function useUserReports(env: Env, status: ReportStatus) {
  const { request } = useAdminApi();
  // request() identity changes with auth/account; hold the latest without
  // re-running the fetch effect (which would remount every card and drop a
  // half-typed note). Same pattern as useVenueClaims.
  const requestRef = useRef(request);
  requestRef.current = request;
  const [reports, setReports] = useState<UserReport[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [tick, setTick] = useState(0);

  const refetch = useCallback(() => setTick((t) => t + 1), []);

  useEffect(() => {
    let mounted = true;
    const load = async () => {
      setLoading(true);
      setError(null);
      try {
        const payload = await requestRef.current<{ reports: UserReport[] }>({
          env,
          path: '/admin/user-reports',
          query: { status },
        });
        if (mounted) setReports(payload.reports ?? []);
      } catch (e) {
        if (mounted) {
          setReports([]);
          setError(e instanceof ApiError ? e.message : 'Failed to load reports');
        }
      } finally {
        if (mounted) setLoading(false);
      }
    };
    load();
    return () => {
      mounted = false;
    };
  }, [env, status, tick]);

  return { reports, setReports, loading, error, refetch };
}

export type ResolveResult =
  | { ok: true; report: UserReport }
  /** `stale`: someone else already handled it, or it is gone — reload the list. */
  | { ok: false; stale: boolean; message: string };

/**
 * Dismiss, restrict, or lift a restriction. The server's message is surfaced
 * as-is: it is written for this screen (e.g. "This report was already handled.").
 */
export function useResolveReport(env: Env) {
  const { request } = useAdminApi();
  const requestRef = useRef(request);
  requestRef.current = request;
  const [busyId, setBusyId] = useState<string | null>(null);

  const resolve = useCallback(
    async (reportId: string, action: ReportAction, note: string): Promise<ResolveResult> => {
      setBusyId(reportId);
      try {
        const report = await requestRef.current<UserReport>({
          env,
          path: `/admin/user-reports/${reportId}/resolve`,
          method: 'POST',
          body: { action, note },
        });
        return { ok: true, report };
      } catch (e) {
        if (e instanceof ApiError) {
          return {
            ok: false,
            stale: e.status === 409 || e.status === 404,
            message: e.message,
          };
        }
        return { ok: false, stale: false, message: 'Something went wrong. Try again.' };
      } finally {
        setBusyId(null);
      }
    },
    [env],
  );

  return { resolve, busyId };
}
